// components/ActivityTracker/ActivityDetailPanel.jsx — Slide-in task detail with progress history
import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Avatar, Button, Field, Input, Select, Spinner, Text, Textarea, Tooltip,
} from "@fluentui/react-components";
import {
  Dismiss24Regular, Mail24Regular, Add24Regular,
  CheckmarkCircle24Regular, Clock24Regular,
  ArrowTrendingLines24Regular, Archive24Regular, ArrowCounterclockwise24Regular,
  Flag24Regular,
} from "@fluentui/react-icons";
import { sendActivityUpdateEmail } from "../../services/emailService";

const PRIORITY_STYLE = {
  High:   { bg: "#FDE7E9", color: "#D13438" },
  Medium: { bg: "#FFF4EC", color: "#E65C00" },
  Low:    { bg: "#E9F5E9", color: "#107C10" },
};
const STATUS_STYLE = {
  "Not Started": { bg: "#F0F0F0", color: "#605E5C" },
  "In Progress": { bg: "#EBF3FF", color: "#005A9E" },
  "On Hold":     { bg: "#FFF4EC", color: "#E65C00" },
  "Completed":   { bg: "#E9F5E9", color: "#107C10" },
  "Closed":      { bg: "#E5E5E5", color: "#3A3A3A" },
};
const CATEGORY_COLORS = {
  Procurement: "#0066FF", Logistics: "#006E6E", Contracts: "#7B2D8B",
  HSE: "#D13438", Projects: "#E65C00", General: "#555",
};

function InfoBlock({ label, value, children }) {
  return (
    <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "12px 14px" }}>
      <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>{label}</Text>
      {children || <Text style={{ fontWeight: 600, color: "#001A3D", fontSize: 13 }}>{value || "—"}</Text>}
    </div>
  );
}

function UpdateTimeline({ updates }) {
  if (!updates || updates.length === 0) {
    return <Text style={{ color: "#999", fontSize: 13, fontStyle: "italic" }}>No updates yet.</Text>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {updates.map((u, i) => {
        const statusStyle = STATUS_STYLE[u.status] || { bg: "#F0F0F0", color: "#605E5C" };
        const isCompleted = u.status?.toLowerCase() === "completed";
        return (
          <div key={u.id} style={{ display: "flex", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: isCompleted ? "#E9F5E9" : "#EBF3FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `2px solid ${isCompleted ? "#107C10" : "#0066FF"}` }}>
                {isCompleted
                  ? <CheckmarkCircle24Regular style={{ color: "#107C10", fontSize: 16 }} />
                  : <ArrowTrendingLines24Regular style={{ color: "#0066FF", fontSize: 14 }} />}
              </div>
              {i < updates.length - 1 && <div style={{ width: 2, flex: 1, background: "#E8EAED", margin: "4px 0" }} />}
            </div>
            <div style={{ flex: 1, paddingBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                <span style={{ background: statusStyle.bg, color: statusStyle.color, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{u.status}</span>
                <span style={{ fontSize: 12, color: "#999" }}>
                  {new Date(u.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  {" · "}{new Date(u.date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              {u.note && (
                <div style={{ background: "#F8F9FC", border: "1px solid #E8EAED", borderRadius: 8, padding: "10px 14px", marginBottom: 6 }}>
                  <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>KEY ACTIVITIES</Text>
                  <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.6 }}>{u.note}</Text>
                </div>
              )}
              {u.lookAhead && (
                <div style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 8, padding: "10px 14px", marginBottom: 4 }}>
                  <Text style={{ fontSize: 11, fontWeight: 700, color: "#0066FF", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>LOOK AHEAD</Text>
                  <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.6 }}>{u.lookAhead}</Text>
                </div>
              )}
              <Text style={{ fontSize: 11, color: "#999" }}>by {u.byName || u.by}</Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ActivityDetailPanel({ activity, onClose, onEdit, onAddProgress, onCloseTask, onReopenTask, canEdit, onToggleCFO, onAddCFORecommendation }) {
  const { instance, accounts } = useMsal();
  const currentUserName  = accounts[0]?.name     || "SCM Staff";
  const currentUserEmail = accounts[0]?.username || "";

  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: "", note: "", lookAhead: "", notifyOwner: false });
  const [saving, setSaving]         = useState(false);
  const [closing, setClosing]       = useState(false);
  const [notifying, setNotifying]   = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);

  // CFO recommendation state
  const [showRecForm, setShowRecForm] = useState(false);
  const [recText, setRecText]         = useState("");
  const [savingRec, setSavingRec]     = useState(false);

  if (!activity) return null;

  const priorityStyle = PRIORITY_STYLE[activity.priority] || PRIORITY_STYLE.Medium;
  const statusStyle   = STATUS_STYLE[activity.status]     || { bg: "#F0F0F0", color: "#605E5C" };
  const catColor      = CATEGORY_COLORS[activity.category] || "#555";
  const daysLeft      = activity.dueDate
    ? Math.ceil((new Date(activity.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  function openUpdateForm() {
    setUpdateForm({
      status: activity.status,
      note: "",
      lookAhead: activity.lookAhead || "",
      notifyOwner: false,
    });
    setNotifyResult(null);
    setShowUpdateForm(true);
  }

  async function submitUpdate() {
    if (!updateForm.note.trim()) return;
    setSaving(true);
    try {
      const result = await onAddProgress(activity.id, {
        status:     updateForm.status,
        note:       updateForm.note.trim(),
        lookAhead:  updateForm.lookAhead.trim(),
        byEmail:    currentUserEmail,
        byName:     currentUserName,
      });
      const hasOwners = (activity.owners?.length > 0) || !!activity.ownerEmail;
      if (updateForm.notifyOwner && hasOwners && result) {
        setNotifying(true);
        const emailResult = await sendActivityUpdateEmail(instance, accounts, {
          activity: result.activity,
          update:   result.update,
          senderName: currentUserName,
        });
        setNotifyResult(emailResult);
        setNotifying(false);
      }
      setShowUpdateForm(false);
    } finally {
      setSaving(false);
    }
  }

  async function submitRecommendation() {
    if (!recText.trim()) return;
    setSavingRec(true);
    try {
      await onAddCFORecommendation?.(activity.id, {
        text: recText.trim(),
        byEmail: currentUserEmail,
        byName: currentUserName,
      });
      setRecText("");
      setShowRecForm(false);
    } finally {
      setSavingRec(false);
    }
  }

  async function handleNotifyOwner() {
    const owners = activity.owners?.length > 0
      ? activity.owners
      : (activity.ownerEmail ? [{ email: activity.ownerEmail, name: activity.ownerName }] : []);
    if (owners.length === 0) return;
    setNotifying(true);
    setNotifyResult(null);
    const latestUpdate = activity.updates?.[0] || {
      id: "manual", date: new Date().toISOString(), by: currentUserEmail, byName: currentUserName,
      status: activity.status, note: "Progress notification sent.", lookAhead: activity.lookAhead || "",
    };
    const result = await sendActivityUpdateEmail(instance, accounts, {
      activity,
      update: latestUpdate,
      senderName: currentUserName,
    });
    setNotifyResult(result);
    setNotifying(false);
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 190, backdropFilter: "blur(2px)" }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 560, height: "100vh",
        background: "#fff", zIndex: 200, boxShadow: "-4px 0 32px rgba(0,0,0,0.14)",
        display: "flex", flexDirection: "column",
      }}>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #EDEBE9", background: "#FAFBFF", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ background: `${catColor}18`, color: catColor, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{activity.category}</span>
                <span style={{ background: priorityStyle.bg, color: priorityStyle.color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{activity.priority}</span>
                <span style={{ background: statusStyle.bg, color: statusStyle.color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{activity.status}</span>
                {activity.userDept && (
                  <span style={{ background: "#F0F4FF", color: "#0044BB", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{activity.userDept}</span>
                )}
                {activity.entity && activity.entity !== "N/A" && (
                  <span style={{ background: "#F0F8F0", color: "#107C10", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{activity.entity}</span>
                )}
              </div>
              <Text size={500} weight="bold" style={{ color: "#001A3D", lineHeight: 1.3, display: "block" }}>{activity.title}</Text>
              {activity.contractor && (
                <Text style={{ fontSize: 12, color: "#666", display: "block", marginTop: 4 }}>
                  Vendor: <strong>{activity.contractor}</strong>
                </Text>
              )}
            </div>
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} style={{ flexShrink: 0 }} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>

          {/* Meta grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <InfoBlock label="DUE DATE">
              <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>
                {activity.dueDate ? new Date(activity.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Not set"}
              </Text>
              {daysLeft !== null && (
                <Text style={{ fontSize: 12, color: daysLeft < 0 ? "#D13438" : daysLeft <= 3 ? "#E65C00" : "#107C10", fontWeight: 600 }}>
                  {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? "Due today" : `${daysLeft}d remaining`}
                </Text>
              )}
            </InfoBlock>
            <InfoBlock label="ASSIGNED TO (RESPONSIBLE PARTY)">
              <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>{activity.assignedToName || "—"}</Text>
              <Text style={{ fontSize: 12, color: "#888" }}>{activity.assignedTo || ""}</Text>
            </InfoBlock>
          </div>

          {/* Owners */}
          {(() => {
            const owners = activity.owners?.length > 0
              ? activity.owners
              : (activity.ownerEmail ? [{ email: activity.ownerEmail, name: activity.ownerName, jobTitle: activity.ownerJobTitle, department: activity.ownerDepartment }] : []);
            if (owners.length === 0) return null;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.4px" }}>
                    CONTRACT OWNER{owners.length > 1 ? "S" : ""}
                  </Text>
                  <Tooltip content={notifyResult?.method === "graph" ? "Email sent!" : `Notify ${owners.length > 1 ? "all owners" : "owner"}`} relationship="label">
                    <Button
                      appearance="subtle" size="small"
                      icon={notifying ? <Spinner size="tiny" /> : <Mail24Regular />}
                      onClick={handleNotifyOwner}
                      disabled={notifying}
                      style={{ color: notifyResult?.method === "graph" ? "#107C10" : "#0066FF" }}
                    >
                      {notifyResult?.method === "graph" ? "Sent!" : owners.length > 1 ? "Notify All" : "Notify"}
                    </Button>
                  </Tooltip>
                </div>
                {owners.map((owner, i) => (
                  <div key={owner.email || i} style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "10px 14px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={owner.name || owner.email} size={28} color="brand" />
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>{owner.name || "Task Owner"}</Text>
                      {(owner.jobTitle || owner.department) && (
                        <Text style={{ fontSize: 11, color: "#555", display: "block" }}>
                          {[owner.jobTitle, owner.department].filter(Boolean).join(" · ")}
                        </Text>
                      )}
                      <Text style={{ fontSize: 12, color: "#0066FF" }}>{owner.email}</Text>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {notifyResult?.method === "mailto" && (
            <div style={{ background: "#FFF4EC", border: "1px solid #FFD6A8", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#E65C00" }}>
              Your email client has been opened with a pre-filled message. Send it to complete the notification.
            </div>
          )}

          {/* CFO Issue flag + recommendations */}
          {(() => {
            const isOwn = activity.assignedTo?.toLowerCase() === currentUserEmail?.toLowerCase();
            const canCFO = canEdit || isOwn;
            const recs = activity.cfoRecommendations || [];
            return (
              <div style={{
                background: activity.cfoIssue ? "#FAF0FF" : "#F8F9FC",
                border: `1.5px solid ${activity.cfoIssue ? "#D4A0E8" : "#E8EAED"}`,
                borderRadius: 10, padding: "12px 16px", marginBottom: 16,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Flag24Regular style={{ fontSize: 16, color: activity.cfoIssue ? "#7B2D8B" : "#888" }} />
                    <Text style={{ fontSize: 11, fontWeight: 700, color: activity.cfoIssue ? "#7B2D8B" : "#888", letterSpacing: "0.4px" }}>
                      CFO ISSUES LIST
                    </Text>
                    {activity.cfoIssue && (
                      <span style={{ background: "#7B2D8B18", color: "#7B2D8B", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>FLAGGED</span>
                    )}
                  </div>
                  {canCFO && onToggleCFO && (
                    <Button
                      appearance="subtle" size="small"
                      onClick={() => { onToggleCFO(activity.id, !activity.cfoIssue); setShowRecForm(false); setRecText(""); }}
                      style={{ color: activity.cfoIssue ? "#D13438" : "#7B2D8B", fontSize: 12 }}
                    >
                      {activity.cfoIssue ? "Remove from CFO Issues" : "Flag for CFO Issues"}
                    </Button>
                  )}
                </div>

                {activity.cfoIssue && (
                  <div style={{ marginTop: 12, borderTop: "1px solid #E8D0F8", paddingTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <Text style={{ fontSize: 11, fontWeight: 700, color: "#7B2D8B", letterSpacing: "0.4px" }}>
                        RECOMMENDATIONS
                        <span style={{ marginLeft: 6, background: "#7B2D8B18", color: "#7B2D8B", borderRadius: 20, padding: "1px 7px", fontSize: 10 }}>
                          {recs.length}
                        </span>
                      </Text>
                      {canCFO && onAddCFORecommendation && !showRecForm && (
                        <Button appearance="subtle" size="small" icon={<Add24Regular />} onClick={() => setShowRecForm(true)} style={{ color: "#7B2D8B" }}>
                          Add
                        </Button>
                      )}
                    </div>

                    {recs.length === 0 && !showRecForm && (
                      <Text style={{ color: "#999", fontSize: 12, fontStyle: "italic" }}>No recommendations yet.</Text>
                    )}
                    {recs.map((r) => (
                      <div key={r.id} style={{ background: "#fff", border: "1px solid #E8D0F8", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                        <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.6, display: "block" }}>{r.text}</Text>
                        <Text style={{ fontSize: 11, color: "#999", display: "block", marginTop: 4 }}>
                          {new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {r.byName || r.by}
                        </Text>
                      </div>
                    ))}

                    {showRecForm && (
                      <div style={{ background: "#fff", border: "1px solid #E8D0F8", borderRadius: 8, padding: "12px 14px", marginTop: 4 }}>
                        <Textarea
                          value={recText}
                          onChange={(_, d) => setRecText(d.value)}
                          placeholder="Enter recommendation for the CFO..."
                          rows={3}
                        />
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <Button
                            appearance="primary" size="small"
                            onClick={submitRecommendation}
                            disabled={!recText.trim() || savingRec}
                            style={{ background: "#7B2D8B" }}
                          >
                            {savingRec ? <Spinner size="tiny" /> : "Save Recommendation"}
                          </Button>
                          <Button appearance="secondary" size="small" onClick={() => { setShowRecForm(false); setRecText(""); }} disabled={savingRec}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Look Ahead */}
          {activity.lookAhead && (
            <div style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#0066FF", letterSpacing: "0.4px", display: "block", marginBottom: 6 }}>LOOK AHEAD</Text>
              <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.7 }}>{activity.lookAhead}</Text>
            </div>
          )}

          {/* Description / Additional Notes */}
          {activity.description && (
            <div style={{ marginBottom: 16 }}>
              <Text style={{ fontSize: 12, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 6 }}>ADDITIONAL NOTES</Text>
              <Text style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>{activity.description}</Text>
            </div>
          )}

          {/* Created by */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, fontSize: 12, color: "#999" }}>
            <Clock24Regular style={{ fontSize: 14 }} />
            <span>Created {new Date(activity.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} by {activity.createdByName || activity.createdBy || "—"}</span>
          </div>

          {/* Update form */}
          {showUpdateForm && (
            <div style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
              <Text style={{ fontSize: 13, fontWeight: 700, color: "#001A3D", display: "block", marginBottom: 14 }}>Log Update</Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field label="Status">
                  <Select value={updateForm.status} onChange={(_, d) => setUpdateForm((p) => ({ ...p, status: d.value }))}>
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </Select>
                </Field>
                <Field label="Key Activities (what was done)" required>
                  <Textarea
                    value={updateForm.note}
                    onChange={(_, d) => setUpdateForm((p) => ({ ...p, note: d.value }))}
                    placeholder="Describe key activities completed this week..."
                    rows={3}
                  />
                </Field>
                <Field label="Look Ahead (next steps)">
                  <Textarea
                    value={updateForm.lookAhead}
                    onChange={(_, d) => setUpdateForm((p) => ({ ...p, lookAhead: d.value }))}
                    placeholder="What is expected to happen next / upcoming milestones..."
                    rows={2}
                  />
                </Field>
                {(activity.owners?.length > 0 || activity.ownerEmail) && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#333" }}>
                    <input
                      type="checkbox"
                      checked={updateForm.notifyOwner}
                      onChange={(e) => setUpdateForm((p) => ({ ...p, notifyOwner: e.target.checked }))}
                      style={{ accentColor: "#0066FF", width: 16, height: 16 }}
                    />
                    <Mail24Regular style={{ fontSize: 16, color: "#0066FF" }} />
                    {activity.owners?.length > 1
                      ? `Notify ${activity.owners.length} owners after saving`
                      : `Notify owner (${activity.owners?.[0]?.email || activity.ownerEmail}) after saving`}
                  </label>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    appearance="primary"
                    onClick={submitUpdate}
                    disabled={saving || !updateForm.note.trim()}
                    style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}
                  >
                    {saving || notifying ? <Spinner size="tiny" /> : "Save Update"}
                  </Button>
                  <Button appearance="secondary" onClick={() => setShowUpdateForm(false)} disabled={saving}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* Update history */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <Text style={{ fontSize: 13, fontWeight: 700, color: "#001A3D", letterSpacing: "0.3px" }}>
                UPDATE HISTORY
                <span style={{ marginLeft: 8, background: "#EBF3FF", color: "#005A9E", borderRadius: 20, padding: "1px 8px", fontSize: 11 }}>
                  {activity.updates?.length || 0}
                </span>
              </Text>
              {canEdit && !showUpdateForm && (
                <Button appearance="subtle" size="small" icon={<Add24Regular />} onClick={openUpdateForm} style={{ color: "#0066FF" }}>
                  Add Update
                </Button>
              )}
            </div>
            <UpdateTimeline updates={activity.updates || []} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #EDEBE9", display: "flex", gap: 8, background: "#FAFBFF", flexShrink: 0, flexWrap: "wrap" }}>
          {canEdit && !showUpdateForm && activity.status !== "Closed" && (
            <>
              <Button appearance="primary" icon={<Add24Regular />} onClick={openUpdateForm} style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}>
                Log Update
              </Button>
              <Button appearance="secondary" onClick={() => onEdit(activity)}>Edit Task</Button>
            </>
          )}
          {canEdit && !showUpdateForm && activity.status !== "Closed" && (
            <Button
              appearance="subtle"
              icon={closing ? <Spinner size="tiny" /> : <Archive24Regular />}
              disabled={closing}
              onClick={async () => { setClosing(true); try { await onCloseTask(activity); } finally { setClosing(false); } }}
              style={{ color: "#605E5C" }}
            >
              Close Task
            </Button>
          )}
          {canEdit && !showUpdateForm && activity.status === "Closed" && (
            <Button
              appearance="outline"
              icon={closing ? <Spinner size="tiny" /> : <ArrowCounterclockwise24Regular />}
              disabled={closing}
              onClick={async () => { setClosing(true); try { await onReopenTask(activity); } finally { setClosing(false); } }}
              style={{ color: "#006E6E", borderColor: "#006E6E" }}
            >
              Reopen Task
            </Button>
          )}
          {(activity.owners?.length > 0 || activity.ownerEmail) && !showUpdateForm && (
            <Button
              appearance="subtle"
              icon={notifying ? <Spinner size="tiny" /> : <Mail24Regular />}
              onClick={handleNotifyOwner}
              disabled={notifying}
              style={{ marginLeft: "auto", color: "#0066FF" }}
            >
              {activity.owners?.length > 1 ? "Notify All Owners" : "Notify Owner"}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
